import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ButtonWhite from '../../../../components/ButtonWhite/ButtonWhite';
import Action from '../../redux/Action';
import { useTranslation } from 'react-multi-lang';
import { setIncompletedProducts, setSelectedIncompletedProduct } from '../../redux/Reducer';
import Cookies from "js-cookie";
import CancelConfirmation from '../bulk-add/CancelConfirmation';
import DeleteConfirmation from '../bulk-add/DeleteConfirmation';
import ProductRepository from '../../../../repositories/ProductRepository';
import PopupUtility from '../../../../utils/popup-utility';

const IncompleteProductList = () => {
    const t = useTranslation();
    const dispatch = useDispatch();
    const data = useSelector((state) => state.reducer.incompletedProducts);

    // Use useState to manage the product list locally
    const [products, setProducts] = useState(data || []);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [indexToDelete, setIndexToDelete] = useState(null);
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    // console.log("🚀 ~ IncompleteProductList ~ products:", products);

    // Sync local state with Redux state when data changes
    useEffect(() => {
        setProducts(data || []);
    }, [data]);

    // Handle product deletion
    const handleDeleteProduct = (index) => {
        setShowDeleteModal(true);
        setIndexToDelete(index);
    };

    const onDeleteRow = () => {
        const productToDelete = products.filter((_, i) => i !== indexToDelete);
        setProducts(productToDelete);
        dispatch(setIncompletedProducts(productToDelete));
        setIndexToDelete(null);
        setShowDeleteModal(false);
    };

    const getProductName = (type, product) => {
        const productEdition = product?.edition !== "" ? " - " + product.edition : "";

        if (type === "Jewellery") {
            return product?.name + " - " + product?.subCategory;
        } else {
            return product?.brand + productEdition;
        }
    };

    function cleanImageUrls(products) {
        return products.map(product => ({
            ...product,
            imageUrl: product.imageUrl.filter(url => url.trim() !== '')
        }));
    }

    function createFormData(products) {
        const formData = new FormData();
        const canBuyBackCompability = Cookies.get("buybackCompability");
        const buybackCompability = canBuyBackCompability ? JSON.parse(canBuyBackCompability) : null;

        products.forEach((product, index) => {
            switch (product?.category) {
                case "Gold Bar":
                    product.imageUrl.forEach((url) => {
                        if (url.trim() !== '') {
                            formData.append(`products[${index}].imageUrl`, url);
                        }
                    });
                    formData.append(`products[${index}].brand`, product.brand || '');
                    formData.append(`products[${index}].sku`, product.sku || '');
                    formData.append(`products[${index}].edition`, product.edition || '');
                    formData.append(`products[${index}].weight`, product.weight);
                    formData.append(`products[${index}].gramsId`, product.gram?.id ?? "");
                    formData.append(`products[${index}].length`, product.length);
                    formData.append(`products[${index}].height`, product.height);
                    formData.append(`products[${index}].width`, product.width);
                    formData.append(`products[${index}].description`, product.description || '');
                    formData.append(`products[${index}].stock`, product.stock);
                    formData.append(`products[${index}].price`, product.price);
                    formData.append(`products[${index}].productCategoryId`, product.categoryId || '');
                    formData.append(`products[${index}].canBuyback`, true);
                    formData.append(`products[${index}].packagingWeight`, product.packagingWeight);
                    break;
                case "Jewellery":
                    product.imageUrl.forEach((url) => {
                        if (url.trim() !== '') {
                            formData.append(`products[${index}].imageUrl`, url);
                        }
                    });
                    formData.append(`products[${index}].name`, product.name || '');
                    formData.append(`products[${index}].caratId`, product?.carat?.id || '');
                    formData.append(`products[${index}].length`, product.length);
                    formData.append(`products[${index}].height`, product.height);
                    formData.append(`products[${index}].width`, product.width);
                    formData.append(`products[${index}].productCategoryId`, product.categoryId || '');
                    formData.append(`products[${index}].productSubCategoryId`, product.subCategoryId || '');
                    formData.append(`products[${index}].productCondition`, product.productCondition);
                    formData.append(`products[${index}].packagingWeight`, product.packagingWeight);
                    formData.append(`products[${index}].sku`, product.sku || '');
                    formData.append(`products[${index}].weight`, product.weight);
                    formData.append(`products[${index}].stock`, product.stock);
                    formData.append(`products[${index}].price`, product.price);
                    formData.append(`products[${index}].promoPrice`, product.promoPrice);
                    if (product?.preciousStoneDetail) {
                        formData.append(`products[${index}].preciousStoneDetail.preciousStoneCertificationCode`, product.preciousStoneDetail.preciousStoneCertificationCode || '');
                        formData.append(`products[${index}].preciousStoneDetail.caratWeight`, product.preciousStoneDetail.caratWeight || '');
                        formData.append(`products[${index}].preciousStoneDetail.preciousStoneWeight`, product.preciousStoneDetail.preciousStoneWeight);
                        formData.append(`products[${index}].preciousStoneDetail.colorGrade`, product.preciousStoneDetail.colorGrade || '');
                        formData.append(`products[${index}].preciousStoneDetail.clarityGrade`, product.preciousStoneDetail.clarityGrade || '');
                    }
                    if (buybackCompability && buybackCompability !== null) {
                        formData.append(`products[${index}].canBuyBack`, true);
                    } else {
                        formData.append(`products[${index}].canBuyBack`, false);
                    }
                    // kurang variants
                    break;
            }
        });
        return formData;
    };

    const handleSubmitBulkAdd = async () => {
        // bersihkan imageurl yang kosong
        const cleanedImageUrls = cleanImageUrls(products);
        // kirim data menggunakan formData
        const formData = createFormData(cleanedImageUrls);
        setLoadingSubmit(true);
        const response = await ProductRepository.bulkAdd(formData);
        if (!response?.error) {
            PopupUtility.success("A new product has been successfully added.");
            setLoadingSubmit(false);
            Action.getPage(dispatch, { page: "all" });
        } else {
            const message = `
                <p className="text-lg mb-3 font-extrabold">${response.message}</p>
                <p className="text-sm font-bold text-gray-400 tracking-wider">Trace Id : ${response.trace_id}</p>
            `;
            PopupUtility.responseValidate("Add Product Failed", message);
            setLoadingSubmit(false);
        }
        // console.log("🚀 ~ handleSubmitBulkAdd ~ response:", response);
        // for (let [key, value] of formData.entries()) {
        //     console.log(`${key}: ${value}`);
        // }
    };

    const checkDisableButton = (items) => {
        const res = items.filter(item => item.isCompleted === false).length !== 0;
        return res;
    };

    return (
        <>
            <div className="container mx-auto p-4 mt-1 bg-[#F5F5F5] rounded-2xl">
                <p className='font-extrabold text-gray-600 mb-2'>PRODUCT LIST ({products.length ?? 0})</p>
                {/* Column Titles */}
                <div className="p-4 flex items-center justify-between mb-2">
                    <div className="w-1/2 font-extrabold text-gray-600">Product Name</div>
                    <div className="w-1/6 font-extrabold text-gray-600 text-center">Product Weight</div>
                    <div className="w-1/6 font-extrabold text-gray-600 text-center">SKU Number</div>
                    <div className="w-1/3 font-extrabold text-gray-600 text-right"></div>
                </div>
                <div className="space-y-1">
                    {products.map((product, index) => (
                        <div key={index} className="bg-white rounded-lg p-4 flex items-center justify-between">
                            <div className="flex items-center w-1/2">
                                {!product?.imageUrl ? (
                                    <img src={process.env.REACT_APP_ASSETS_IMAGE + "blank-image.svg"} alt="" className='w-[35px] h-[35px] mr-2' />
                                ) : (
                                    <img src={product?.imageUrl?.[0]} alt="" className='w-[35px] h-[35px] mr-2' />
                                )}
                                <span className='font-semibold'>{getProductName(product.category, product)}</span>
                            </div>
                            <div className="w-1/6 text-center">{product.weight} gram</div>
                            <div className="w-1/6 text-center">{product.sku}</div>
                            <div className="w-1/3 space-x-2 text-right">
                                {!product?.isCompleted && (<button onClick={() => {
                                    dispatch(setSelectedIncompletedProduct(product));
                                    Action.getPage(dispatch, { page: "edit-incompleted-products" });
                                }} className="text-green-primary underline hover:no-underline mr-2">Complete Details</button>)}
                                <button
                                    onClick={() => handleDeleteProduct(index)}
                                    className="text-red-500 underline hover:no-underline"
                                >
                                    Delete Product
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="flex justify-end gap-2 mt-3">
                    <ButtonWhite
                        title={t("general.cancel")}
                        onClick={() => setShowCancelModal(true)}
                    />
                    <button
                        disabled={checkDisableButton(products) || loadingSubmit}
                        onClick={handleSubmitBulkAdd}
                        type="submit"
                        className={`button md:w-36  text-xs tracking-widest font-bold  border-2  ${checkDisableButton(products) || loadingSubmit ? "bg-gray-300 border-gray-300 text-slate-500 cursor-not-allowed" : "cursor-pointer bg-green-secondary border-green-secondary text-gray-primary hover:bg-gray-primary hover:text-green-secondary"} border-opacity-40 rounded-xl py-2`}
                    >
                        {t("general.proceed")}
                    </button>
                </div>
            </div>
            {showCancelModal && (<CancelConfirmation onCancel={() => setShowCancelModal(false)} />)}
            {showDeleteModal && (<DeleteConfirmation onDelete={() => onDeleteRow()} onCancel={() => setShowDeleteModal(false)} />)}
        </>
    );
};

export default IncompleteProductList;